import { useAuth } from '@auth/useAuth'
import AppPaths from '@globals/paths'
import AnonPaths from '@globals/paths/anon'
import {
  register404Listener,
  register503Listener,
  registerRewardEmailRequiredListener,
  unauthenticatedListener,
} from '@kudos/http-client'
import RouteLoading from '@shared/routing/RouteLoading'
import { useEffect, useRef } from 'react'
import { Location, Outlet, useLocation, useNavigate } from 'react-router'
import RootLayout from '../layouts/RootLayout'

export const AwaitAuth = () => {
  const { isAuthenticated, isLoading, initializeFromStorage, clearAuth, isTenantScope } = useAuth()
  const hasInitialized = useRef<boolean>(false)
  const tenantScopeRef = useRef<boolean>(isTenantScope)
  const navigate = useNavigate()
  const location = useLocation()
  // Kept current so the global RewardEmailRequired listener (registered once)
  // always redirects from — and back to — the user's actual location.
  const locationRef = useRef<Location>(location)

  useEffect(() => {
    tenantScopeRef.current = isTenantScope
  }, [isTenantScope])

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (!hasInitialized.current) {
      initializeFromStorage()
      // Register listener to detect 401s and clear the auth
      // Only clear auth if we are in tenant scope; ignore 401s during user-scope (multi-tenant selection)
      unauthenticatedListener(() => {
        if (tenantScopeRef.current) {
          clearAuth()
        }
      })
      // Register listener to detect 503 (maintenance) and redirect to maintenance page
      register503Listener(() => navigate(AnonPaths.maintenance()))
      // Register listener to detect 404 (not found) and redirect to not found page
      register404Listener(() => navigate(AnonPaths.notFound()))
      // Centralised front door (epic SYN-7280): any reward endpoint returning
      // RewardEmailRequired sends a deskless member to the email-capture screen.
      registerRewardEmailRequiredListener(() => {
        const current = locationRef.current
        const { pathname } = current
        // Never redirectfrom the capture screen itself (which would loop and clobber `from`).
        if (pathname === AppPaths.tenant.rewardsEmailRequired()) {
          return
        }
        navigate(AppPaths.tenant.rewardsEmailRequired(), {
          state: { from: `${current.pathname}${current.search}` },
        })
      })
    }
    hasInitialized.current = true
  }, [clearAuth, initializeFromStorage, navigate])

  if (isLoading) {
    return <RouteLoading />
  }

  return (
    <RootLayout isAuthenticated={isAuthenticated} isTenantScope={isTenantScope}>
      <Outlet />
    </RootLayout>
  )
}
